"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

const services = [
  {
    title: "Micro Business Loan",
    image: "/projects/profile.jpg",
    description:
      "Tailored small-scale financing solutions designed to empower entrepreneurs and small businesses.",
    lists: [
      "Working capital loans",
      "Equipment financing",
      "Trade credit facilities",
    ],
    details:
      "Our micro loans feature flexible repayment terms, competitive interest rates, and quick approval processes to support your business growth and operational needs.",
  },
  {
    title: "Agricultural Financing",
    image: "/projects/profile2.jpg",
    description:
      "Specialized financial products for farmers and agribusinesses to boost productivity.",
    lists: [
      "Seasonal crop financing",
      "Livestock loans",
      "Irrigation equipment loans",
    ],
    details:
      "We offer customized agricultural loans with crop-cycle aligned repayment schedules, input financing, and technical support to enhance farm productivity and sustainability.",
  },
];

const otheryegna = [
  {
    title: "Corporate Customer Loan",
    description:
      "Yegna MFI provides a loan tailored for employees or customers of YMF's strategic partner companies. It offers lower interest rates than standard consumer loans, with terms from 12 months to 120 months with monthly or quarterly repayments.",
    image: "/projects/corporatesaving.jpg",
  },
  {
    title: "Project Financing",
    description:
      "Project Financing Loan is a type of credit facility designed to fund specific business projects or income-generating activities (IGAs) for micro-entrepreneurs, small businesses, or community-based enterprises. Unlike general-purpose loans, project financing loans are tied to a particular investment, such as purchasing equipment, expanding a business, or starting a new venture.",
    image: "/projects/projectfinancing.jpg",
  },
];

const projects = [
  {
    title: "Consumer Loan",
    description:
      "Consumer loans for employees are designed for personal purposes, such as covering medical expenses, paying education fees, purchasing household equipment, and other personal expenditures. These loans help meet various personal and social commitments beyond business-related needs.",
    image: "/projects/consumerloan.jpg",
  },
  {
    title: "Agriculture Loan",
    description:
      "This loan will be availed to middle income farmer borrowers for purchasing agricultural inputs: like fertilizer, improved seed, land rent, purchase of oxen and for fattening activities.",
    image: "/projects/picagri.jpg",
  },
];

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const text = "Business & Trade Financing Solutions";

export default function ServicebuisnessCards() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  return (
    <div className="mt-30 mb-20 space-y-16 pt-28 overflow-x-hidden sm:pt-10 md:pt-6 lg:pt-0">
      {/* Projects Section */}
      <div className="mt-4 px-4 lg:mx-auto lg:max-w-[1500px]" id="projects">
        <div className="py-2">
          <div className="mt-16 space-y-28">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <div
                  className={`flex flex-col ${index % 2 ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-10 md:gap-16`}
                >
                  {/* Text Section */}
                  <motion.div
                    className="z-10 space-y-6 md:w-1/2"
                    initial={{ x: index % 2 ? 50 : -50 }}
                    whileInView={{ x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="flex items-start">
                      <motion.div
                        className="mr-4 h-8 w-1"
                        style={{ background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryLight})` }}
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        transition={{ duration: 0.5 }}
                      />
                      <div>
                        <motion.h3
                          className="text-3xl font-bold"
                          style={{ color: colors.primary }}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          {project.title}
                        </motion.h3>
                        <motion.div
                          className="mt-3 h-1 w-16"
                          style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          transition={{ delay: 0.4 }}
                        />
                      </div>
                    </div>

                    <motion.p
                      className="text-lg"
                      style={{ color: colors.textDark }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      {project.description}
                    </motion.p>
                  </motion.div>

                  {/* Image Section */}
                  <motion.div
                    className="relative md:w-1/2"
                    whileHover={{ scale: 0.98 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="overflow-hidden rounded-xl shadow-lg">
                      <Image
                        src={project.image}
                        width={1200}
                        height={1900}
                        alt={project.title}
                        className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>

                    {/* Animated decorative elements */}
                    <motion.div
                      className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full"
                      style={{ backgroundColor: `${colors.primaryLight}20` }}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.4 }}
                    />
                    <motion.div
                      className="absolute -right-4 -top-4 h-12 w-12 rounded-lg"
                      style={{ backgroundColor: `${colors.primary}20` }}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.6 }}
                    />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Sector-Specific Financing Section */}
      <div className="mt-20 px-4 lg:mx-auto lg:max-w-[1500px]" id="special-loans">
        <div className="py-16 pl-1" style={{ backgroundColor: colors.white }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-24">
              {otheryegna.map((loan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Decorative elements */}
                  <motion.div
                    className="absolute -left-8 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full opacity-70"
                    style={{ backgroundColor: `${colors.primary}10` }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.4 }}
                  />

                  <div className="relative flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-16">
                    {/* Image with decorative border */}
                    <div className="lg:w-5/12">
                      <motion.div
                        className="relative aspect-[4/3] overflow-hidden rounded-lg"
                        whileHover={{ scale: 0.98 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="absolute inset-0 z-10 border-8 border-white opacity-30" />
                        <Image
                          src={loan.image}
                          alt={loan.title}
                          fill
                          className="object-cover"
                        />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent, transparent, ${colors.primary}30)` }} />
                      </motion.div>
                    </div>

                    {/* Content */}
                    <div className="lg:w-7/12">
                      <motion.div
                        initial={{ x: 30 }}
                        whileInView={{ x: 0 }}
                        transition={{ duration: 0.6 }}
                      >
                        <div className="mb-6 flex items-start">
                          <motion.div
                            className="mr-4 mt-1 h-8 w-1"
                            style={{ background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryLight})` }}
                            initial={{ scaleY: 0 }}
                            whileInView={{ scaleY: 1 }}
                            transition={{ duration: 0.5 }}
                          />
                          <div>
                            <motion.h3
                              className="text-3xl font-bold"
                              style={{ color: colors.primary }}
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              transition={{ delay: 0.2 }}
                            >
                              {loan.title}
                            </motion.h3>
                            <motion.p
                              className="mt-4 text-lg"
                              style={{ color: colors.textDark }}
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              transition={{ delay: 0.3 }}
                            >
                              {loan.description}
                            </motion.p>
                          </div>
                        </div>

                        {/* Features as inline tags */}
                        <motion.div
                          className="mt-8 flex flex-wrap gap-3"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                        >
                          {[
                            `Up to ${index % 2 === 0 ? "36" : "24"} months term`,
                            `Flexible collateral`,
                            `Competitive rates`,
                            `Local support`,
                          ].map((item, i) => (
                            <motion.span
                              key={i}
                              className="rounded-md px-3 py-1.5 text-sm font-medium"
                              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                              initial={{ scale: 0 }}
                              whileInView={{ scale: 1 }}
                              transition={{ delay: 0.2 + i * 0.1 }}
                            >
                              {item}
                            </motion.span>
                          ))}
                        </motion.div>

                        {/* CTA with animated arrow */}
                        <motion.div
                          className="mt-10"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          <Link
                            href="#"
                            className="group inline-flex items-center text-lg font-semibold transition-colors"
                            style={{ color: colors.primary }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = colors.primaryDark }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = colors.primary }}
                          >
                            Explore {loan.title} options
                            <motion.span
                              className="ml-2 inline-block transition-transform group-hover:translate-x-1"
                              whileHover={{ x: 3 }}
                            >
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                                />
                              </svg>
                            </motion.span>
                          </Link>
                        </motion.div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Section-wide CTA */}
            <motion.div
              className="mt-20 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="inline-block rounded-lg p-0.5" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                <Link
                  href="/contact"
                  className="flex items-center rounded-md px-8 py-4 text-lg font-bold transition-all hover:bg-opacity-90"
                  style={{ backgroundColor: colors.white, color: colors.primary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primaryLight;
                    e.currentTarget.style.color = colors.white;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.white;
                    e.currentTarget.style.color = colors.primary;
                  }}
                >
                  Speak with a financing specialist
                  <svg
                    className="ml-3 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}